import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-700.css'
import './mobile.css';
import App from './App.tsx'

/** Load Sentry only after an uncaught error — keeps ~140 KiB off the critical path. */
function registerSentryOnError(): void {
  const environment = import.meta.env.APP_ENV || 'production';
  const sentryDsn = environment === 'production'
    ? import.meta.env.VITE_SENTRY_DSN_PRODUCTION
    : import.meta.env.VITE_SENTRY_DSN_STAGING;

  if (!sentryDsn) {
    return;
  }

  let initStarted = false;

  const initSentry = (): void => {
    if (initStarted) {
      return;
    }
    initStarted = true;

    void import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        environment,
        sendDefaultPii: true,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
      });

      const client = Sentry.getClient();
      if (client && !client.getIntegrationByName('Replay')) {
        client.addIntegration(
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        );
      }
    });
  };

  window.addEventListener('error', initSentry, { once: true });
  window.addEventListener('unhandledrejection', initSentry, { once: true });
}

registerSentryOnError();

const rootElement = document.getElementById('root');

if (!rootElement) {
    throw new Error('Failed to find the root element');
}

try {
    const root = createRoot(rootElement);
    root.render(
        <StrictMode>
            <App />
        </StrictMode>
    );
} catch (error) {
    console.error('Failed to render app:', error);
    document.getElementById('app-boot-loader')?.remove();
    rootElement.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
            <h1>Error Loading Application</h1>
            <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
            <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error instanceof Error ? error.stack : String(error)}</pre>
        </div>
    `;
}
